/**
 * Recipe Store
 *
 * Manages recipe state using Zustand with persistence.
 * Provides CRUD operations, search, and filtering functionality for recipes.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  Recipe,
  RecipeFormData,
  RecipeSearchParams,
  PdfImportResult,
} from '../types/recipe';
import api from '../services/api';

interface RecipeState {
  recipes: Recipe[];
  currentRecipe: Recipe | null;
  isLoading: boolean;
  error: string | null;
  searchParams: RecipeSearchParams;

  // Actions
  fetchRecipes: (params?: RecipeSearchParams) => Promise<void>;
  fetchRecipe: (id: number) => Promise<void>;
  createRecipe: (data: RecipeFormData) => Promise<Recipe>;
  updateRecipe: (id: number, data: RecipeFormData) => Promise<Recipe>;
  deleteRecipe: (id: number) => Promise<void>;
  importFromPdf: (files: File[]) => Promise<PdfImportResult>;
  setCurrentRecipe: (recipe: Recipe | null) => void;
  setSearchParams: (params: RecipeSearchParams) => void;
  clearError: () => void;
}

/**
 * Recipe Store with Zustand
 *
 * Uses persist middleware to cache recipes for offline access.
 * Provides CRUD operations with optimistic updates and error handling.
 */
export const useRecipeStore = create<RecipeState>()(
  persist(
    (set) => ({
      recipes: [],
      currentRecipe: null,
      isLoading: false,
      error: null,
      searchParams: {},

      fetchRecipes: async (params?: RecipeSearchParams) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.get('/recipes', { params });

          const recipes = response.data.recipes || response.data.data || response.data;
          set({
            recipes: Array.isArray(recipes) ? recipes : [],
            isLoading: false,
            searchParams: params || {},
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch recipes',
          });
          throw error;
        }
      },

      fetchRecipe: async (id: number) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.get(`/recipes/${id}`);

          set({
            currentRecipe: response.data.recipe || response.data.data || response.data,
            isLoading: false,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch recipe',
          });
          throw error;
        }
      },

      createRecipe: async (data: RecipeFormData) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.post('/recipes', { recipe: data });
          const newRecipe = response.data.recipe || response.data.data || response.data;

          set((state) => ({
            recipes: [...state.recipes, newRecipe],
            currentRecipe: newRecipe,
            isLoading: false,
          }));

          return newRecipe;
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to create recipe',
          });
          throw error;
        }
      },

      updateRecipe: async (id: number, data: RecipeFormData) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.put(`/recipes/${id}`, { recipe: data });
          const updatedRecipe = response.data.recipe || response.data.data || response.data;

          set((state) => ({
            recipes: state.recipes.map((recipe) =>
              recipe.id === id ? updatedRecipe : recipe
            ),
            currentRecipe: state.currentRecipe?.id === id ? updatedRecipe : state.currentRecipe,
            isLoading: false,
          }));

          return updatedRecipe;
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to update recipe',
          });
          throw error;
        }
      },

      deleteRecipe: async (id: number) => {
        set({ isLoading: true, error: null });

        try {
          await api.delete(`/recipes/${id}`);

          set((state) => ({
            recipes: state.recipes.filter((recipe) => recipe.id !== id),
            currentRecipe: state.currentRecipe?.id === id ? null : state.currentRecipe,
            isLoading: false,
          }));
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to delete recipe',
          });
          throw error;
        }
      },

      importFromPdf: async (files) => {
        set({ isLoading: true, error: null });
        try {
          const formData = new FormData();
          files.forEach((file) => formData.append('files[]', file));
          const response = await api.post('/recipes/import_pdf', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 180_000,
          });
          const result: PdfImportResult = response.data;
          if (result.recipes.length > 0) {
            set((state) => ({ recipes: [...state.recipes, ...result.recipes], isLoading: false }));
          } else {
            set({ isLoading: false });
          }
          return result;
        } catch (error) {
          set({ isLoading: false, error: error instanceof Error ? error.message : 'PDF import failed' });
          throw error;
        }
      },

      setCurrentRecipe: (recipe: Recipe | null) => {
        set({ currentRecipe: recipe });
      },

      setSearchParams: (params: RecipeSearchParams) => {
        set({ searchParams: params });
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'recipe-storage', // localStorage key
      version: 2, // Bump to invalidate stale cached data from old format
      partialize: (state) => ({
        // Persist recipes and currentRecipe for offline access
        // Don't persist loading/error states or search params
        recipes: state.recipes,
        currentRecipe: state.currentRecipe,
      }),
      merge: (persisted, current) => ({
        ...current,
        ...(persisted as Partial<RecipeState>),
        // Ensure recipes is always an array (guards against stale/corrupt localStorage)
        recipes: Array.isArray((persisted as Partial<RecipeState>)?.recipes)
          ? (persisted as Partial<RecipeState>).recipes!
          : [],
      }),
    }
  )
);
